import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import {
  LayoutDashboard,
  FileText,
  Wrench,
  MessageSquare,
  Settings,
  ChevronLeft,
  ChevronRight,
  Phone,
  LogOut,
  X,
  ClipboardList,
  HelpCircle,
  Star,
  Search,
  Image,
} from "lucide-react";
import { cn } from "../../lib/utils";
import Logo from "../../assets/acr-logo.jpeg";
import { removeData } from "../../LocalConnection/LocalConnection.js";
import {
  server_post_data,
  get_all_WebsiteManagement,
} from "../../ServiceConnection/serviceconnection.js";

// ─── Constants ────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { label: "Overview", icon: LayoutDashboard, path: "/dashboard", end: true },
  { label: "Blog Posts", icon: FileText, path: "/blogs" },
  { label: "Blog Category", icon: FileText, path: "/blogcategories" },
  { label: "Gallery Manager", icon: Image, path: "/gallery_manager" },
  { label: "FAQ Manager", icon: HelpCircle, path: "/faqs" },
  { label: "Testimonials", icon: Star, path: "/testimonials" },
  { label: "SEO Manager", icon: Search, path: "/seo" },
  { label: "Image Library", icon: Image, path: "/images" },
  { label: "Contact Messages", icon: MessageSquare, path: "/messages" },
  { label: "Estimate Requests", icon: ClipboardList, path: "/estimates" },
  { label: "Settings", icon: Settings, path: "/settings" },
];

const SESSION_KEYS = [
  "admin_id",
  "admin_name",
  "admin_email",
  "admin_designation",
  "admin_type",
  "allow_access",
  "admin_image",
  "designation_name",
  "access_permission",
  "admin_name_with_gender",
];

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const Sidebar = ({ mobileOpen, onMobileClose }) => {
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [phoneNo, setPhoneNo] = useState("");

  // ─── Fetch website contact number ───────────────────────────────────────
  const fetchContactNo = useCallback(async () => {
    try {
      const fd = new FormData();
      fd.append("flag", "1");

      const response = await server_post_data(get_all_WebsiteManagement, fd);

      if (!response.data.error) {
        const data = response.data.message.data_websiteManagement?.[0] || {};
        setPhoneNo(data.website_contact_no_first || "");
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchContactNo();
  }, [fetchContactNo]);

  // ─── Logout ───────────────────────────────────────────────────────────────
  const handleLogout = () => {
    SESSION_KEYS.forEach((key) => removeData(key));
    navigate("/login", { replace: true });
  };

  // ─── UI ───────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={onMobileClose}
        />
      )}

      <aside
        className={cn(
          "fixed left-0 top-0 z-50 flex h-screen flex-col bg-gradient-sidebar transition-all duration-300",
          collapsed ? "w-[72px]" : "w-64",
          "lg:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        {/* Logo */}
        <div
          className={cn(
            "flex h-16 shrink-0 items-center border-b border-white/10 px-4",
            collapsed ? "justify-center" : "justify-between",
          )}
        >
          {!collapsed && (
            <div className="flex items-center gap-2.5">
              <img
                className="h-10 rounded-lg"
                src={Logo}
                alt="Logo"
                title="Logo"
              />
              <div>
                <p className="text-sm font-bold leading-none text-white">ACR</p>
                <p className="mt-0.5 text-[10px] leading-none text-white/40">
                  Dashboard
                </p>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-primary shadow-glow">
              <Wrench className="h-4 w-4 text-white" />
            </div>
          )}
          {/* Mobile close */}
          <button
            onClick={onMobileClose}
            className="text-white/40 transition-colors hover:text-white lg:hidden"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-5">
          {!collapsed && (
            <p className="mb-3 px-2 text-[10px] font-semibold uppercase tracking-widest text-white/30">
              Main Menu
            </p>
          )}
          <ul className="space-y-1">
            {NAV_ITEMS.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.end}
                  onClick={onMobileClose}
                  className={({ isActive }) =>
                    cn(
                      "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
                      isActive
                        ? "bg-gradient-primary text-white shadow-glow"
                        : "text-white/60 hover:bg-white/8 hover:text-white",
                    )
                  }
                >
                  <item.icon
                    className={cn(
                      "h-[18px] w-[18px] shrink-0",
                      collapsed && "mx-auto",
                    )}
                  />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom section */}
        <div className="shrink-0 space-y-2 border-t border-white/10 px-3 py-4">
          {/* Logout */}
          {!collapsed ? (
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-xs font-medium text-white/40 transition-colors hover:bg-red-500/10 hover:text-red-400"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              Logout
            </button>
          ) : (
            <button
              onClick={handleLogout}
              title="Logout"
              className="flex w-full items-center justify-center rounded-xl p-2.5 text-white/40 transition-colors hover:bg-red-500/10 hover:text-red-400"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}

          {/* Support contact — dynamic phone from API */}
          {!collapsed && (
            <div className="rounded-xl bg-white/5 p-3">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/30">
                Support
              </p>
              {phoneNo ? (
                <a
                  href={`tel:${phoneNo.replace(/\D/g, "")}`}
                  className="flex items-center gap-2 text-xs text-white/60 transition-colors hover:text-white"
                >
                  <Phone className="h-3.5 w-3.5 text-primary" />
                  {phoneNo}
                </a>
              ) : (
                <span className="flex items-center gap-2 text-xs text-white/20">
                  <Phone className="h-3.5 w-3.5" />—
                </span>
              )}
            </div>
          )}

          {/* Collapsed — phone icon only with tooltip */}
          {collapsed && phoneNo && (
            <a
              href={`tel:${phoneNo.replace(/\D/g, "")}`}
              title={phoneNo}
              className="flex w-full items-center justify-center rounded-xl p-2.5 text-white/40 transition-colors hover:bg-white/10 hover:text-white"
            >
              <Phone className="h-4 w-4" />
            </a>
          )}
        </div>

        {/* Collapse toggle — desktop only */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 hidden h-6 w-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-foreground lg:flex"
        >
          {collapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronLeft className="h-3 w-3" />
          )}
        </button>
      </aside>

      {/* Spacer */}
      <div
        className={cn(
          "hidden shrink-0 transition-all duration-300 lg:block",
          collapsed ? "w-[72px]" : "w-64",
        )}
      />
    </>
  );
};

export default Sidebar;
